/**
 * StickyFreeAnalysisCTA — bottom-right yellow pill that hovers above all
 * content after the user scrolls past the fold. The single highest-ROI
 * conversion booster on long-scroll landing pages.
 *
 * Hidden on: dashboard, admin, /affiliate, /tools, /lifetime, /preview —
 * places where the CTA is either irrelevant or already the primary action.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const HIDDEN_PREFIXES = [
  "/dashboard", "/admin", "/affiliate", "/tools",
  "/lifetime", "/preview", "/auth", "/legal", "/tvorba-webu",
];
const DISMISS_KEY = "trp_sticky_cta_dismissed";
const DISMISS_TTL_HOURS = 8;

const LABELS = {
  sk: "Analýza ZADARMO",
  cs: "Analýza ZDARMA",
  en: "FREE Analysis",
  de: "Kostenlose Analyse",
  pl: "Darmowa Analiza",
  hu: "Ingyenes elemzés",
  fr: "Analyse GRATUITE",
  es: "Análisis GRATIS",
  it: "Analisi GRATIS",
  pt: "Análise GRÁTIS",
  nl: "Gratis analyse",
};

export function StickyFreeAnalysisCTA() {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Respect previous dismissal for 8 hours.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) return;
      const ts = parseInt(raw, 10);
      if (Date.now() - ts < DISMISS_TTL_HOURS * 3600_000) setDismissed(true);
      else localStorage.removeItem(DISMISS_KEY);
    } catch { /* ignore */ }
  }, []);

  // Scroll-trigger after 600px or 60% of viewport, whichever is smaller.
  useEffect(() => {
    if (dismissed) return;
    const threshold = Math.min(600, window.innerHeight * 0.6);
    const onScroll = () => setVisible(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed]);

  const isHidden = HIDDEN_PREFIXES.some(p => location.pathname.startsWith(p));
  if (isHidden || dismissed || !visible) return null;

  const path = location.pathname;
  const csRoute = /^\/(gratulujeme\/cz|tvorba-webu\/cz|cz\/)/.test(path);
  const skRoute = /^\/(gratulujeme\/sk|tvorba-webu\/sk|riesenia|vsetko-v-jednom|pre-|sluzby|rebricek-zlepseni|bezpecnost-webu)/.test(path);
  const label = csRoute ? LABELS.cs : skRoute ? LABELS.sk : LABELS[language] || LABELS.en;
  const funnelInput = path.startsWith("/gratulujeme/") ? "[data-testid=congrats-url-input]" : null;

  const goScan = () => {
    if (funnelInput && document.querySelector(funnelInput)) {
      const el = document.querySelector(funnelInput);
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => el.focus(), 600);
    } else if (location.pathname === "/") {
      // Smooth scroll to the URL input on the home page.
      const input = document.querySelector("[data-testid=url-input]");
      const hero = document.querySelector("#hero-section");
      const target = hero || input;
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
      // Focus after scroll completes.
      setTimeout(() => input?.focus(), 700);
    } else {
      navigate("/#hero-section");
    }
  };

  const dismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 animate-in slide-in-from-bottom-4 fade-in duration-300"
      data-testid="sticky-free-analysis-cta"
    >
      <button
        type="button"
        onClick={goScan}
        className="group flex items-center gap-2 px-5 py-3.5 bg-[#FFE600] text-black font-black uppercase tracking-wider text-sm sm:text-base rounded-full shadow-2xl shadow-[#FFE600]/30 hover:scale-[1.04] hover:bg-yellow-300 transition-all"
        data-testid="sticky-cta-button"
      >
        <Search className="w-4 h-4 sm:w-5 sm:h-5" />
        {label}
        <span className="hidden sm:inline-block text-[10px] font-mono bg-black text-[#FFE600] px-2 py-0.5 rounded-full ml-1">{funnelInput ? "5 s" : "30 s"}</span>
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="dismiss"
        className="w-7 h-7 flex items-center justify-center bg-black/70 hover:bg-black text-white/70 hover:text-white rounded-full"
        data-testid="sticky-cta-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default StickyFreeAnalysisCTA;
